import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Conversation {
  id: string;
  conversation_summary: string | null;
  last_message_at: string;
  started_at: string;
}

interface ChatContextType {
  isChatOpen: boolean;
  isChatMinimized: boolean;
  hasStartedConversation: boolean;
  initialQuery: string;
  conversationTitle: string | null;
  chatKey: number;
  isNewChat: boolean;
  currentConversationId: string | null;
  conversations: Conversation[];
  openChat: (query?: string) => void;
  openChatWithQuery: (query: string, title?: string) => void;
  closeChat: () => void;
  minimizeChat: () => void;
  expandChat: () => void;
  startNewChat: () => void;
  markConversationStarted: () => void;
  switchConversation: (conversationId: string) => void;
  setConversations: (conversations: Conversation[]) => void;
  setCurrentConversationId: (id: string | null) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');
  const [conversationTitle, setConversationTitle] = useState<string | null>(null);
  const [chatKey, setChatKey] = useState(0);
  const [isNewChat, setIsNewChat] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);

  // Load conversation state from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('cn_chat_started');
    if (stored === 'true') {
      setHasStartedConversation(true);
    }
  }, []);

  const openChat = (query?: string) => {
    if (query) {
      setInitialQuery(query);
      setIsNewChat(false);
    }
    setIsChatOpen(true);
    setIsChatMinimized(false);
  };

  const openChatWithQuery = (query: string, title?: string) => {
    setInitialQuery(query);
    setConversationTitle(title || null);
    setIsNewChat(true);
    setCurrentConversationId(null);
    setChatKey(prev => prev + 1);
    setIsChatOpen(true);
    setIsChatMinimized(false);
  };

  const closeChat = () => {
    setIsChatOpen(false);
    setIsChatMinimized(false);
  };

  const minimizeChat = () => {
    setIsChatMinimized(true);
  };

  const expandChat = () => {
    setIsChatMinimized(false);
    setIsChatOpen(true);
  };

  const startNewChat = () => {
    setInitialQuery('');
    setConversationTitle(null);
    setIsNewChat(true);
    setCurrentConversationId(null); // Reset conversation ID to trigger new conversation creation
    setChatKey(prev => prev + 1);
    setIsChatOpen(true);
    setIsChatMinimized(false);
  };

  const markConversationStarted = () => {
    setHasStartedConversation(true);
    localStorage.setItem('cn_chat_started', 'true');
  };

  const switchConversation = (conversationId: string) => {
    setCurrentConversationId(conversationId);
    setChatKey(prev => prev + 1);
    setIsNewChat(false);
    setInitialQuery('');
    setConversationTitle(null);
    setIsChatOpen(true);
  };

  return (
    <ChatContext.Provider
      value={{
        isChatOpen,
        isChatMinimized,
        hasStartedConversation,
        initialQuery,
        conversationTitle,
        chatKey,
        isNewChat,
        currentConversationId,
        conversations,
        openChat,
        openChatWithQuery,
        closeChat,
        minimizeChat,
        expandChat,
        startNewChat,
        markConversationStarted,
        switchConversation,
        setConversations,
        setCurrentConversationId,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
